import React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Clock, TrendingUp, TrendingDown } from "lucide-react"
import { useCalculateEmployeeHours } from "@/hooks/useEmployeeHoursBank"
import { useAuth } from "@/hooks/useAuth"

interface HoursBankCardProps {
  employeeId?: string
  condominiumId?: string
  showEmployeeName?: boolean
  employeeName?: string
  simplified?: boolean
}

export const HoursBankCard = ({ 
  employeeId, 
  condominiumId, 
  showEmployeeName = false, 
  employeeName,
  simplified = false
}: HoursBankCardProps) => {
  const { profile } = useAuth()
  const { data: hoursData, isLoading } = useCalculateEmployeeHours(employeeId, condominiumId)

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 animate-spin" />
            <span>Carregando banco de horas...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!hoursData) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            Nenhum dado de banco de horas encontrado
          </div>
        </CardContent>
      </Card>
    )
  }

  const formatHours = (hours: number) => {
    const absHours = Math.abs(hours)
    const wholeHours = Math.floor(absHours)
    const minutes = Math.round((absHours - wholeHours) * 60)
    
    if (minutes === 0) {
      return `${wholeHours}h`
    }
    return `${wholeHours}h ${minutes}m`
  }

  const formatHoursColon = (hours: number) => {
    const absHours = Math.abs(hours)
    const wholeHours = Math.floor(absHours)
    const minutes = Math.round((absHours - wholeHours) * 60)
    const sign = hours >= 0 ? '+' : '-'
    
    return `${sign}${wholeHours}:${minutes.toString().padStart(2, '0')}`
  }

  const isPositive = hoursData.balance >= 0
  const balanceColor = isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
  const balanceBgColor = isPositive ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-red-50 dark:bg-red-950/30"
  const balanceIcon = isPositive ? TrendingUp : TrendingDown

  // Simplified layout for operators
  if (simplified) {
    return (
      <Card className="border-2">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-bold">
            Meu Saldo de Horas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className={`text-7xl font-bold ${balanceColor} mb-4`}>
              {formatHoursColon(hoursData.balance)}
            </div>
            <div className={`text-xl font-semibold ${balanceColor}`}>
              {isPositive ? '✅ Horas positivas' : '⚠️ Horas negativas'}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Full layout for managers
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          💰 Banco de Horas
          {showEmployeeName && employeeName && (
            <Badge variant="secondary">{employeeName}</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Controle das horas trabalhadas vs. horas exigidas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Horas Trabalhadas</div>
            <div className="text-3xl font-bold text-primary">
              {formatHours(hoursData.total_worked)}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Horas Exigidas</div>
            <div className="text-3xl font-bold text-muted-foreground">
              {formatHours(hoursData.required_hours)}
            </div>
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${balanceBgColor} border-2 ${isPositive ? 'border-emerald-200 dark:border-emerald-800' : 'border-red-200 dark:border-red-800'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-muted-foreground">
              {isPositive ? '📈 SALDO POSITIVO' : '📉 SALDO NEGATIVO'}
            </div>
            {React.createElement(balanceIcon, { className: `h-6 w-6 ${balanceColor}` })}
          </div>
          <div className={`text-4xl font-bold ${balanceColor} mb-3`}>
            {hoursData.balance >= 0 ? '+' : ''}{formatHours(hoursData.balance)}
          </div>
          <div className="text-sm">
            {isPositive ? (
              <span className={balanceColor}>
                ✅ Você tem <strong>{formatHours(hoursData.balance)}</strong> de horas extras acumuladas!
              </span>
            ) : (
              <span className={balanceColor}>
                ⚠️ Você deve <strong>{formatHours(Math.abs(hoursData.balance))}</strong> de horas ao banco
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default HoursBankCard